import React, { useEffect, useState } from 'react';

import {
  ContentCardContainer,
  ContentCardHeader,
  ContentDivider
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/UtilizationCardContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useMainEntity,
  useProductEntity,
  useRetailerDisplayName,
  useRetailerId,
  useRetailerSku
} from 'src/utils/Hooks/reduxSelectors';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import { EyeShow, EyeHide } from 'src/components/SvgIcons/SvgIcons';
import { PhraseWithMetadata } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/KeywordsProductLevelDetails';
import Papa from 'papaparse';
import saveAs from 'file-saver';
import { useMainTimePeriod, useMetricFormatter } from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import { KeywordFilter } from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/types';
import {
  ChartDataAdapter,
  KeywordBreakdownData,
  SectionKeywords
} from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/adapters/types';
import BeaconProChart from 'src/components/BeaconV2Components/ExperimentalLayout/ContentV3/components/Summary/Visualizations/BeaconProChart';

// This prevents react query from refetching the data between usage and productivity (since they use the same underlying data)
const KEYWORD_FILTER_TO_QUERY_KEY = {
  [KeywordFilter.Usage]: 'keyword-usage-or-productivity',
  [KeywordFilter.Productivity]: 'keyword-usage-or-productivity',
  [KeywordFilter.Popularity]: 'keyword-popularity'
};

const EXPORT_KEYWORD_TITLE = {
  [KeywordFilter.Usage]: 'KeywordUsage',
  [KeywordFilter.Productivity]: 'KeywordProductivity',
  [KeywordFilter.Popularity]: 'KeywordPopularity'
};

const SECTION_ORDER = ['Title', 'Bullet', 'Description'];

interface ProductLevelKeywordPerformanceDetailsProps {
  keywordFilter: KeywordFilter;
  queryFn: () => Promise<ChartDataAdapter<KeywordBreakdownData>>;
  renderSubtitle: () => string | React.ReactNode;
}

type Weight = {
  keyword: string;
  subcat_level_weight: number;
  sku_level_weight: number;
};

type Group = {
  groupId: string;
  totalSubcatWeight: number;
  weights: Weight[];
};

type Section = {
  name: string;
  groups: Group[];
};

const commonExportHeader = [
  'Retailer ID',
  'Retailer Name',
  'Retailer SKU',
  'UPC',
  'Title',
  'Brand',
  'Category',
  'Subcategory',
  'Availability Status',
  'Availability Status Code',
  'Section'
];
const exportHeaders = {
  Usage: [
    ...commonExportHeader,
    'Keyword Usage',
    'Keyword',
    'Organic Traffic Value (Clicks) (Product)',
    'Organic Traffic Value (Clicks) (Sub-Category)'
  ],
  Productivity: [
    ...commonExportHeader,
    'Keyword Productivity',
    'Keyword',
    'Organic Traffic Value (Clicks) (Product)',
    'Organic Traffic Value (Clicks) (Sub-Category)',
    'Productivity'
  ],
  Popularity: [...commonExportHeader, 'Keyword Popularity', 'Keyword', 'Search Volume']
};

export const ProductLevelKeywordPerformanceDetails = ({
  queryFn,
  keywordFilter,
  renderSubtitle
}: ProductLevelKeywordPerformanceDetailsProps) => {
  const { type } = useMainEntity();
  const [hideKeywords, setHideKeywords] = useState(false);

  // Used to assign a unique id to each phrase to ensure only one popup is open at a time, can be refactored to use id generated from within adapter
  let phraseIndex = 1;
  const [activePhraseId, setActivePhraseId] = useState<number | null>(null);
  const retailerId = useRetailerId();
  const retailerName = useRetailerDisplayName();
  const retailerSku = useRetailerSku();
  const { productEntity } = useProductEntity();
  const { endWeek } = useMainTimePeriod();
  const formatMetric = useMetricFormatter();

  const extractWeightsWithSection = (
    sections: Section[]
  ): { keyword: string; subcat_level_weight: number; sku_level_weight: number; sectionName: string }[] => {
    const weightsList: {
      keyword: string;
      subcat_level_weight: number;
      sku_level_weight: number;
      sectionName: string;
    }[] = [];

    sections.forEach((section) => {
      const keywordSet = new Set();
      section.groups.forEach((group) => {
        group.weights.forEach((weight) => {
          if (!keywordSet.has(weight.keyword)) {
            weightsList.push({
              keyword: weight.keyword,
              subcat_level_weight: weight.subcat_level_weight,
              sku_level_weight: weight.sku_level_weight,
              sectionName: section.name
            });
            keywordSet.add(weight.keyword);
          }
        });
      });
    });

    // based on the SECTION ORDER we want to sort the export
    return weightsList.sort((a, b) => {
      const aBase = a.sectionName.split(' ')[0];
      const bBase = b.sectionName.split(' ')[0];

      const aNum = parseInt(a.sectionName.split(' ')[1], 10) || 0;
      const bNum = parseInt(b.sectionName.split(' ')[1], 10) || 0;

      if (aBase === bBase) {
        // Sort by number
        return aNum - bNum;
      }

      const aIndex = SECTION_ORDER.indexOf(aBase);
      const bIndex = SECTION_ORDER.indexOf(bBase);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) {
        return -1;
      }
      if (bIndex !== -1) {
        return 1;
      }
      return 0;
    });
  };

  const generateExcelExport = (data) => {
    const sections = data?.weightedKeywordsAndPhrasesResponse?.weightedKeywordPhrases?.weightedKeywordPhrases?.sections;
    const scores = data?.summaryResponse;
    const weightsList = extractWeightsWithSection(sections);

    const exportRows = weightsList.map(({ keyword, subcat_level_weight, sku_level_weight, sectionName }) => ({
      'Retailer ID': retailerId,
      'Retailer Name': retailerName,
      'Retailer SKU': retailerSku,
      UPC: productEntity?.upc,
      Title: productEntity?.title,
      Brand: productEntity?.brandName,
      Category: productEntity?.categoryName,
      Subcategory: productEntity?.subCategoryName,
      'Availability Status': productEntity?.availabilityStatus,
      'Availability Status Code': productEntity?.availabilityStatusCode,
      Section: sectionName,

      ...(keywordFilter === KeywordFilter.Usage
        ? { 'Keyword Usage': `${(scores?.keywordUsageScore * 100).toFixed(2)}%` }
        : {}),
      ...(keywordFilter === KeywordFilter.Popularity
        ? { 'Keyword Popularity': `${(scores?.keywordPopularityScore * 100).toFixed(2)}%` }
        : {}),
      ...(keywordFilter === KeywordFilter.Productivity
        ? { 'Keyword Productivity': `${(scores?.keywordProductivityScore * 100).toFixed(2)}%` }
        : {}),

      Keyword: keyword,

      'Subcategory Weight': formatMetric(subcat_level_weight, METRICTYPE.VOLUME, {
        showFullValue: true
      }),
      'Organic Traffic Value (Clicks) (Product)': formatMetric(sku_level_weight, METRICTYPE.VOLUME, {
        showFullValue: true
      }),

      'Organic Traffic Value (Clicks) (Sub-Category)': formatMetric(subcat_level_weight, METRICTYPE.VOLUME, {
        showFullValue: true
      }),
      ...(keywordFilter === KeywordFilter.Productivity
        ? { Productivity: sku_level_weight === 0 ? 'Unproductive' : 'Productive' }
        : {}),
      ...(keywordFilter === KeywordFilter.Popularity
        ? {
            'Search Volume': formatMetric(subcat_level_weight, METRICTYPE.VOLUME, {
              showFullValue: true
            })
          }
        : {})
    }));

    try {
      const csv = Papa.unparse({
        fields: exportHeaders[keywordFilter],
        data: exportRows
      });
      const csvWithBOM = `\ufeff${csv}`;
      const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8' });
      saveAs(blob, `Product_${retailerSku}_${EXPORT_KEYWORD_TITLE[keywordFilter]}_${endWeek}.csv`);
    } catch (e) {
      console.error('Error generating export:', e);
    }
  };

  useEffect(() => {
    setHideKeywords(false);
    setActivePhraseId(null);
  }, [keywordFilter]);

  const icon = hideKeywords ? (
    <EyeShow style={{ width: 16, height: 16 }} />
  ) : (
    <EyeHide style={{ width: 16, height: 16 }} />
  );

  if (type !== 'product') {
    return null;
  }

  return (
    <BeaconProChart
      name={`product-level-keyword-performance-details-${KEYWORD_FILTER_TO_QUERY_KEY[keywordFilter]}`}
      queryFn={queryFn}
    >
      <BeaconProChart.Header
        menuButton={(data, isLoading) => {
          const isDisabled =
            !data ||
            isLoading ||
            !data?.weightedKeywordsAndPhrasesResponse ||
            !data?.weightedKeywordsAndPhrasesResponse?.weightedKeywordPhrases ||
            data?.weightedKeywordsAndPhrasesResponse?.weightedKeywordPhrases?.weightedKeywordPhrases?.sections
              ?.length === 0;
          return (
            <Flex alignItems="center" gap="md">
              <SlButton
                disabled={isDisabled}
                buttonSx={{ padding: '0px 0px 0px 8px', height: '32px', width: '143px', justifyContent: 'stretch' }}
                onClick={() => {
                  setHideKeywords(!hideKeywords);
                  setActivePhraseId(null);
                }}
              >
                <Flex gap="sm" alignItems="center">
                  {icon}
                  <Text variant="subtitle2">{`${hideKeywords ? 'Show' : 'Hide'} keywords`}</Text>
                </Flex>
              </SlButton>
              <SlButton disabled={isLoading} onClick={() => generateExcelExport(data)}>
                Export
              </SlButton>
            </Flex>
          );
        }}
      >
        <BeaconProChart.Title>
          <Text variant="h4">Keyword Performance by Element</Text>
        </BeaconProChart.Title>
      </BeaconProChart.Header>
      <BeaconProChart.Subtitle
        render={(_: ChartDataAdapter<KeywordBreakdownData>, isLoading: boolean) => {
          if (isLoading) {
            return 'Loading subtitle...';
          }

          return renderSubtitle();
        }}
      />

      <BeaconProChart.Visualization
        render={(adapter: ChartDataAdapter<KeywordBreakdownData>) => {
          const { sections } = adapter.transform();
          return (
            <Flex flexDirection="column">
              {sections.map((item: SectionKeywords, sectionIndex: number) => (
                <div key={sectionIndex}>
                  <ContentCardContainer>
                    <ContentCardHeader title={item.name} />
                    <Text variant="body1">
                      <Flex gap="xs" flexWrap="wrap" alignItems="center">
                        {item.content.map((content, contentIndex) =>
                          content.metadata ? (
                            <PhraseWithMetadata
                              id={phraseIndex++}
                              key={contentIndex}
                              {...content}
                              hideKeywords={hideKeywords}
                              activePhraseId={activePhraseId}
                              setActivePhraseId={setActivePhraseId}
                              keywordFilter={keywordFilter}
                            />
                          ) : (
                            content.phrase.split(' ').map((word, wordIndex) => (
                              <Text
                                // Prevent multiple lines without PhraseWithMetadata from squishing together in order to maintain consistent line height across entire content
                                lineHeight={hideKeywords ? 'normal' : '2'}
                                variant="body2"
                                key={`${wordIndex}-${word}`}
                              >
                                {word}
                              </Text>
                            ))
                          )
                        )}
                      </Flex>
                    </Text>
                  </ContentCardContainer>
                  {sectionIndex < sections.length - 1 && (
                    <Flex justifyContent="center">
                      <ContentDivider />
                    </Flex>
                  )}
                </div>
              ))}
            </Flex>
          );
        }}
      />
    </BeaconProChart>
  );
};
